import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Critical pages loaded immediately (landing page for first visit)
import LandingPage from './pages/landing-page';
import NotFound from "pages/NotFound";

// All other pages lazy-loaded
// High-priority pages (preload on hover/mount)
const EdadRealAssessmentQuiz = lazy(() => import(/* webpackPrefetch: true */ './pages/edad-real-assessment-quiz'));
const QuizResultsDashboard = lazy(() => import(/* webpackPrefetch: true */ './pages/quiz-results-dashboard'));

// Medium-priority pages
const ContactLeadCapture = lazy(() => import('./pages/contact-lead-capture'));
const BlogContentHub = lazy(() => import('./pages/blog-content-hub'));
const ProtectedVSLAccess = lazy(() => import('./pages/protected-vsl-access'));
const VSLLanding = lazy(() => import('./pages/vsl-landing'));
const TestimonialsPage = lazy(() => import('./pages/testimonials'));
const BlogArticle = lazy(() => import('./pages/blog-article'));
const BlogManagement = lazy(() => import('./pages/blog-management'));
const Login = lazy(() => import('./pages/auth/Login'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const SetPassword = lazy(() => import('./pages/auth/SetPassword'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MemberDashboard = lazy(() => import('./pages/MemberDashboardNew'));
const MemberProfileSettings = lazy(() => import('./pages/MemberProfileSettings'));
const MemberContentLibrary = lazy(() => import('./pages/MemberContentLibrary'));

// Course System
const Courses = lazy(() => import('./pages/coach/Courses'));
const CourseAnalytics = lazy(() => import('./pages/coach/CourseAnalytics'));
const MemberCourses = lazy(() => import('./pages/MemberCourses'));
const MemberCourseDetail = lazy(() => import('./pages/MemberCourseDetail'));

// Exercise Library & Coaching Platform
const ExerciseLibrary = lazy(() => import('./pages/ExerciseLibrary'));
const WorkoutPrograms = lazy(() => import('./pages/WorkoutPrograms'));
const WorkoutBuilder = lazy(() => import('./pages/WorkoutBuilder'));
const MemberAssignments = lazy(() => import('./pages/MemberAssignments'));
const WorkoutAssignmentDetail = lazy(() => import('./pages/WorkoutAssignmentDetail'));
const WorkoutSession = lazy(() => import('./pages/WorkoutSession'));
const MemberProgress = lazy(() => import('./pages/MemberProgress'));
const MemberStats = lazy(() => import('./pages/MemberStats'));
const PersonalRecords = lazy(() => import('./pages/PersonalRecords'));
const ExerciseAnalytics = lazy(() => import('./pages/ExerciseAnalytics'));
const CoachAnalytics = lazy(() => import('./pages/CoachAnalytics'));
const BodyMeasurements = lazy(() => import('./pages/BodyMeasurements'));
const ProgressPhotos = lazy(() => import('./pages/ProgressPhotos'));
const Badges = lazy(() => import('./pages/Badges'));
const HabitTracker = lazy(() => import('./pages/HabitTracker'));
const Leaderboards = lazy(() => import('./pages/Leaderboards'));

// AI Generation System
const AIWorkoutGenerator = lazy(() => import('./pages/coach/AIWorkoutGenerator'));
const AIMealPlanGenerator = lazy(() => import('./pages/coach/AIMealPlanGenerator'));
const ExerciseVideoImporter = lazy(() => import('./pages/coach/ExerciseVideoImporter'));
const MealPlans = lazy(() => import('./pages/coach/MealPlans'));
const MealPlanDetail = lazy(() => import('./pages/coach/MealPlanDetail'));
const MemberMealPlanDetail = lazy(() => import('./pages/MemberMealPlanDetail'));

// Legal Pages
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const LegalNotice = lazy(() => import('./pages/legal/LegalNotice'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));

// Newsletter System Routes
const AutomatedContentCurationEngine = lazy(() => import('./pages/automated-content-curation-engine'));
const SubscriberSegmentationManager = lazy(() => import('./pages/subscriber-segmentation-manager'));

// EFEM Premium Payment Pages
const EFEMPremiumPayment = lazy(() => import('./pages/efem-premium-payment'));
const PaymentSuccess = lazy(() => import('./pages/efem-premium-payment/components/PaymentSuccess'));
const PaymentCancelled = lazy(() => import('./pages/efem-premium-payment/components/PaymentCancelled'));

// EFEM Starter Payment Pages
const EFEMStarterPayment = lazy(() => import('./pages/efem-starter-payment'));
const StarterPaymentSuccess = lazy(() => import('./pages/efem-starter-payment/components/PaymentSuccess'));
const StarterPaymentCancelled = lazy(() => import('./pages/efem-starter-payment/components/PaymentCancelled'));

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <Suspense fallback={<LoadingFallback />}>
          <RouterRoutes>
            {/* Landing page loads immediately (not lazy) */}
            <Route path="/" element={<LandingPage />} />

            {/* Authentication Routes - Wrapped with AuthProvider */}
            <Route path="/login" element={<ProtectedRoute><Login /></ProtectedRoute>} />
            <Route path="/signup" element={<ProtectedRoute><Signup /></ProtectedRoute>} />
            <Route path="/set-password" element={<ProtectedRoute><SetPassword /></ProtectedRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/callback" element={<ProtectedRoute><AuthCallback /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/member/dashboard" element={<ProtectedRoute><MemberDashboard /></ProtectedRoute>} />
            <Route path="/member/profile-settings" element={<ProtectedRoute><MemberProfileSettings /></ProtectedRoute>} />
            <Route path="/member/content-library" element={<ProtectedRoute><MemberContentLibrary /></ProtectedRoute>} />

            {/* Course System Routes - Protected */}
            <Route path="/coach/courses" element={<ProtectedRoute><Courses /></ProtectedRoute>} />
            <Route path="/coach/course-analytics" element={<ProtectedRoute><CourseAnalytics /></ProtectedRoute>} />
            <Route path="/member/courses" element={<ProtectedRoute><MemberCourses /></ProtectedRoute>} />
            <Route path="/member/course/:courseId" element={<ProtectedRoute><MemberCourseDetail /></ProtectedRoute>} />

            {/* Newsletter System Routes - Protected */}
            <Route path="/automated-content-curation-engine" element={<ProtectedRoute><AutomatedContentCurationEngine /></ProtectedRoute>} />
            <Route path="/subscriber-segmentation-manager" element={<ProtectedRoute><SubscriberSegmentationManager /></ProtectedRoute>} />

            {/* Exercise Library & Coaching Platform - Protected */}
            <Route path="/exercises" element={<ProtectedRoute><ExerciseLibrary /></ProtectedRoute>} />
            <Route path="/workout-programs" element={<ProtectedRoute><WorkoutPrograms /></ProtectedRoute>} />
            <Route path="/workout-builder/new" element={<ProtectedRoute><WorkoutBuilder /></ProtectedRoute>} />
            <Route path="/workout-builder/:programId" element={<ProtectedRoute><WorkoutBuilder /></ProtectedRoute>} />
            <Route path="/member-assignments" element={<ProtectedRoute><MemberAssignments /></ProtectedRoute>} />
            <Route path="/workout-assignment/:assignmentId" element={<ProtectedRoute><WorkoutAssignmentDetail /></ProtectedRoute>} />
            <Route path="/workout-session/:assignmentId/:dayId" element={<ProtectedRoute><WorkoutSession /></ProtectedRoute>} />
            <Route path="/member/progress" element={<ProtectedRoute><MemberProgress /></ProtectedRoute>} />
            <Route path="/member/stats" element={<ProtectedRoute><MemberStats /></ProtectedRoute>} />
            <Route path="/member/personal-records" element={<ProtectedRoute><PersonalRecords /></ProtectedRoute>} />
            <Route path="/member/body-measurements" element={<ProtectedRoute><BodyMeasurements /></ProtectedRoute>} />
            <Route path="/member/progress-photos" element={<ProtectedRoute><ProgressPhotos /></ProtectedRoute>} />
            <Route path="/member/exercise-analytics" element={<ProtectedRoute><ExerciseAnalytics /></ProtectedRoute>} />
            <Route path="/member/badges" element={<ProtectedRoute><Badges /></ProtectedRoute>} />
            <Route path="/member/habits" element={<ProtectedRoute><HabitTracker /></ProtectedRoute>} />
            <Route path="/member/leaderboards" element={<ProtectedRoute><Leaderboards /></ProtectedRoute>} />
            <Route path="/member/meal-plan/:assignmentId" element={<ProtectedRoute><MemberMealPlanDetail /></ProtectedRoute>} />
            <Route path="/coach/analytics" element={<ProtectedRoute><CoachAnalytics /></ProtectedRoute>} />
            <Route path="/coach/ai-workout-generator" element={<ProtectedRoute><AIWorkoutGenerator /></ProtectedRoute>} />
            <Route path="/coach/ai-meal-plan-generator" element={<ProtectedRoute><AIMealPlanGenerator /></ProtectedRoute>} />
            <Route path="/coach/exercise-video-importer" element={<ProtectedRoute><ExerciseVideoImporter /></ProtectedRoute>} />
            <Route path="/coach/meal-plans" element={<ProtectedRoute><MealPlans /></ProtectedRoute>} />
            <Route path="/coach/meal-plan/:planId" element={<ProtectedRoute><MealPlanDetail /></ProtectedRoute>} />

            {/* Main Routes */}
            <Route path="/contact-lead-capture" element={<ContactLeadCapture />} />
            <Route path="/quiz-results-dashboard" element={<QuizResultsDashboard />} />
            <Route path="/blog-content-hub" element={<BlogContentHub />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/blog-management" element={<ProtectedRoute><BlogManagement /></ProtectedRoute>} />
            <Route path="/edad-real-assessment-quiz" element={<EdadRealAssessmentQuiz />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/protected-vsl-access" element={<ProtectedVSLAccess />} />
            <Route path="/vsl/:leadId" element={<VSLLanding />} />

            {/* Legal Routes */}
            <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/politica-privacidad" element={<PrivacyPolicy />} />
            <Route path="/legal/terms-of-service" element={<TermsOfService />} />
            <Route path="/terminos-servicio" element={<TermsOfService />} />
            <Route path="/legal/legal-notice" element={<LegalNotice />} />
            <Route path="/aviso-legal" element={<LegalNotice />} />
            <Route path="/legal/cookie-policy" element={<CookiePolicy />} />
            <Route path="/politica-cookies" element={<CookiePolicy />} />

            {/* EFEM Premium Payment Routes */}
            <Route path="/efem-premium/payment" element={<EFEMPremiumPayment />} />
            <Route path="/efem-premium/success" element={<PaymentSuccess />} />
            <Route path="/efem-premium/cancelled" element={<PaymentCancelled />} />

            {/* EFEM Starter Payment Routes */}
            <Route path="/efem-starter/payment" element={<EFEMStarterPayment />} />
            <Route path="/efem-starter/success" element={<StarterPaymentSuccess />} />
            <Route path="/efem-starter/cancelled" element={<StarterPaymentCancelled />} />

            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;